import { useState } from "react";
import { ApiError } from "./api-error";

export interface FetchOptions
{
    method?: "POST" | "GET" | "PUT" | "DELETE" | "PATCH";
    body?: any;
    path?: string;
    query?: { [key: string]: any};
}

export interface CustomFetch {
    get: (id?: any) => Promise<FetchResponse>;
    getQuery: (id?: any, query?: any) => Promise<FetchResponse>;
    post: (body: any, query?: any) => Promise<FetchResponse>;
    postPath: (path: string, body: any, query?: any) => Promise<FetchResponse>;
    put: (id: string, body: any) => Promise<FetchResponse>;
    del: (id: string) => Promise<FetchResponse>;
    isLoading: boolean;
}

export interface FetchResponse {
    status: number;
    success: boolean;
    result: any;
    errors: ApiError[]
}

export const useFetch = (endpoint: string): CustomFetch => {
    const [isLoading, setIsLoading] = useState(false);

    const defaultHeader: any = {
        Accept: "application/json",
        "Content-type": "application/json; charset=UTF-8"
    };

    const customFetch = async (fetchOptions: FetchOptions, headers = defaultHeader) => {
        setIsLoading(true);

        const options: RequestInit = {
            method: fetchOptions.method ?? (fetchOptions.body ? "POST" : "GET"),
            body: fetchOptions.body,
            headers,
        };

        const requestVerificationTokenElement = document.querySelector("meta[name=RequestVerificationToken]");
        const token = requestVerificationTokenElement?.getAttribute("content") || "";

        options.headers = {
            ...options.headers,
            RequestVerificationToken: token
        };

        if (fetchOptions.body) {
            options.body = JSON.stringify(fetchOptions.body);
        }

        let url = buildUrl(fetchOptions);
        
        return await fetch(url, options)
            .then(async (response: Response) => {
                let fetchResponse: FetchResponse = {
                    success: response.ok,
                    status: response.status,
                    result: {},
                    errors: []
                };

                if (response.ok && response.status != 204) {
                    fetchResponse.result = await response.json();
                } else if (response.status == 422 || response.status == 400) {
                    fetchResponse.errors = await response.json();
                } else if (response.status == 404 || response.status == 204) {
                    // No changes needed
                }

                setIsLoading(false);

                return fetchResponse;
            })
            .catch(error => {
                console.error("Error fetching data", error);
                setIsLoading(false);
                throw new Error(error);
            });
    };


    function buildUrl (options: FetchOptions): string {
        // Remove leading slash from path
        let path = (options.path ?? "").replace(/^\/+/, "");
        // Remove trailing slash from base
        let base = endpoint.replace(/\/+$/, "");
        let url = `${base}${path ? `/${path}` : ""}`;
        let queryString = "";
        const keys = Object.keys(options.query ?? {});
        if (keys.length > 0) {
            const query = options.query!;
            const queryParts = keys
                .map(key => {
                    const value = (query[key] instanceof Date) ? query[key].toISOString() : query[key].toString();
                    return `${key}=${value}`;
                });
            queryString = `?${queryParts.join("&")}`;
        }

        url = `${url}${queryString}`
        return url;
    }

    const get = (id: any = null) : Promise<FetchResponse> => {
        return customFetch({ method: "GET", path: id });
    };

    const getQuery = (id: any = null, query: any = null) : Promise<FetchResponse> => {
        return customFetch({ method: "GET", path: id, query: query });
    };

    const post = (body: any, query: any = null) : Promise<FetchResponse> => {
        if (!body) {
            throw new Error("to make a post you must provide a body");
        }
        return customFetch({ method: "POST", body: body, query: query });
    };

    const postPath = (path: string, body: any, query: any = null) : Promise<FetchResponse> => {
        if (!body) {
            throw new Error("to make a post you must provide a body");
        }
        return customFetch({ method: "POST", path: path, body: body, query: query });
    };

    const put = (id: string, body: any) : Promise<FetchResponse> => {
        if (!id || !body) {
            throw new Error("to make a put you must provide the id and the body");
        }
        return customFetch({ method: "PUT", body: body, path: id});
    };

    const del = (id: string) : Promise<FetchResponse> => {
        if (!id) {
            throw new Error("to make a delete you must provide the id and the body");
        }
        return customFetch({ method: "DELETE", path: id });
    };

    return {
        get,
        getQuery,
        post,
        postPath,
        put,
        del,
        isLoading
    };
};